import { Grid, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { CustomSelect } from '../../../../../../../../../common/Styleguide/Common/Select';
import { MetricsOptions, MetricsTimeframeInput, renderTimeframeFilterOptions } from '../../../../../../../../helpers/metricsHelpers';
import { Option } from '@mui/base';
import { StyledDatePicker } from '../../../../../../../../../common/Styleguide/Common/DatePicker';
import moment from 'moment';
import { convertPeriodToOption, graphDateInputFormat } from '../../../../../../../../../common/datetime';
import { StyledBodyText } from '../../../../../../../../../common/Styleguide/Common/Text';
import InfoIcon from '@mui/icons-material/Info';

type TimeframeFilterProps = {
  value: MetricsTimeframeInput;
  setValue: (value: MetricsTimeframeInput) => void;
  showLabel?: boolean;
  showTooltip?: boolean;
};

const TimeframeFilter: React.FC<TimeframeFilterProps> = ({ value, setValue, showLabel = true, showTooltip = true }) => {
  const [customPeriod, setCustomPeriod] = useState<MetricsOptions>(convertPeriodToOption('LAST_7_DAYS'));
  const serializePeriod = (period: MetricsTimeframeInput): string => JSON.stringify(period);
  const deserializePeriod = (period: string): MetricsTimeframeInput => JSON.parse(period);

  const TIMEFRAME_FILTER_OPTIONS = renderTimeframeFilterOptions(customPeriod);

  useEffect(() => {
    if (value.category === 'custom') {
      setValue({
        category: 'custom',
        period: 'CUSTOM',
        options: customPeriod,
        preDefinedTimeframe: true,
      });
    }
  }, [customPeriod, value, setValue]);

  return (
    <Grid container direction="row" sx={{ width: 'auto', alignItems: 'center' }}>
      {showLabel && (
        <Grid item>
          <StyledBodyText sx={{ fontWeight: 700 }}>Stats Timeframe</StyledBodyText>
        </Grid>
      )}
      {showTooltip && (
        <Grid item justifyContent="center" alignItems={'center'}>
          <Tooltip
            sx={{ height: '100%', width: 15, m: 1, mt: '14px' }}
            title="Note that some details are not available with a custom date range"
          >
            <InfoIcon />
          </Tooltip>
        </Grid>
      )}
      <Grid item>
        <CustomSelect
          style={{ height: 40 }}
          value={serializePeriod(value)}
          onChange={(_, value) => {
            if (typeof value === 'string') {
              setValue(deserializePeriod(value));
            }
          }}
        >
          {TIMEFRAME_FILTER_OPTIONS.map((option, i) => {
            const value = serializePeriod(option.value);
            return (
              <Option key={i} value={value}>
                {option.label}
              </Option>
            );
          })}
        </CustomSelect>
      </Grid>

      {value.category === 'custom' && (
        <Grid item>
          <StyledDatePicker
            format="DD MMMM YYYY"
            value={moment.utc(customPeriod.from)}
            minDate={moment.utc('2023-09-01').endOf('day')}
            maxDate={moment.utc()}
            onChange={(v) => {
              const newCustomPeriod = {
                from: graphDateInputFormat(moment.utc(v)),
                until: graphDateInputFormat(moment.utc(customPeriod.until)),
              };
              setCustomPeriod(newCustomPeriod);
            }}
            slotProps={{ textField: (params) => <TextField size="small" {...params} /> }}
            sx={{ ml: 0.5 }}
          />

          <StyledDatePicker
            format="DD MMMM YYYY"
            value={moment.utc(customPeriod.until)}
            minDate={moment.utc('2023-09-01').endOf('day')}
            maxDate={moment.utc()}
            onChange={(v) => {
              const newCustomPeriod = {
                from: graphDateInputFormat(moment.utc(customPeriod.from)),
                until: graphDateInputFormat(moment.utc(v)),
              };
              setCustomPeriod(newCustomPeriod);
            }}
            slotProps={{ textField: (params) => <TextField size="small" {...params} /> }}
            sx={{ ml: 0.5 }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default TimeframeFilter;
